import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

// Content strategy for SourcePro Infotech (Ameet Desai, via Kush). Full-screen
// deck, one idea per screen, presented live. Built from the 2026-09-30 call, the
// questionnaire returned 2026-10-08, the New Horizon product deck (V8) and the
// expert council of 2026-10-13. No pricing and no competitor names by design.

const SLIDES = [
    'Your answers',
    'The position',
    'The cycle',
    'Reach',
    'Trust and sales',
    'The loops',
    'The face',
    'Your time',
    'Next',
];

const Eyebrow = ({ children }) => (
    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-6">{children}</div>
);

const Slide = ({ id, children }) => (
    <section id={id} className="snap-start min-h-[100svh] w-full flex items-center px-6 sm:px-10 py-24">
        <div className="w-full max-w-3xl mx-auto">{children}</div>
    </section>
);

const Row = ({ k, v, tone = 'indigo' }) => {
    const toneClass =
        tone === 'rose' ? 'text-rose-400' : tone === 'emerald' ? 'text-emerald-400' : tone === 'amber' ? 'text-amber-300' : 'text-indigo-400';
    return (
        <div className="flex items-baseline gap-4 sm:gap-6 border-t border-white/10 py-4">
            <div className={`shrink-0 w-24 sm:w-36 text-xs sm:text-sm font-bold tracking-wide ${toneClass}`}>{k}</div>
            <div className="text-slate-300 text-sm sm:text-base leading-relaxed min-w-0">{v}</div>
        </div>
    );
};

const Mix = ({ n, label, sub, tone }) => (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 flex flex-col gap-2">
        <div className={`text-5xl sm:text-6xl font-black ${tone}`}>{n}</div>
        <div className="text-white font-bold text-base sm:text-lg">{label}</div>
        <div className="text-slate-400 text-sm leading-relaxed">{sub}</div>
    </div>
);

export default function SourceProStrategy() {
    const scroller = useRef(null);
    const [active, setActive] = useState(0);

    useEffect(() => {
        document.title = 'SocialSEO | SourcePro';
    }, []);

    useEffect(() => {
        const el = scroller.current;
        if (!el) return undefined;
        const onScroll = () => {
            const i = Math.round(el.scrollTop / el.clientHeight);
            setActive(Math.max(0, Math.min(SLIDES.length - 1, i)));
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    const go = (i) => {
        const el = scroller.current;
        if (el) el.scrollTo({ top: i * el.clientHeight, behavior: 'smooth' });
    };

    return (
        <div className="font-sans text-white h-[100svh] overflow-hidden relative selection:bg-indigo-500/30">
            <div className="fixed inset-0 bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-25%] left-[-15%] w-[65%] h-[65%] bg-indigo-600/20 blur-[170px] rounded-full mix-blend-screen" />
                <div className="absolute bottom-[-25%] right-[-15%] w-[65%] h-[65%] bg-amber-500/10 blur-[170px] rounded-full mix-blend-screen" />
            </div>

            <header className="fixed top-0 left-0 right-0 z-50 px-6 sm:px-10 py-5 bg-[#07090E]/70 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-2 max-w-3xl mx-auto">
                    <span className="font-bold tracking-tight text-lg">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-1">/</span>
                    <span className="text-white/70 font-medium text-sm truncate">SourcePro Infotech</span>
                </div>
            </header>

            <div className="hidden md:flex fixed right-7 top-1/2 -translate-y-1/2 z-50 flex-col gap-3">
                {SLIDES.map((label, i) => (
                    <button
                        key={label}
                        onClick={() => go(i)}
                        aria-label={label}
                        className={`w-2 h-2 rounded-full transition-all ${i === active ? 'bg-indigo-400 scale-150' : 'bg-white/25 hover:bg-white/50'}`}
                    />
                ))}
            </div>

            <div ref={scroller} className="h-[100svh] overflow-y-scroll snap-y snap-mandatory scroll-smooth">

                {/* 1. Your answers */}
                <Slide id="answers">
                    <Eyebrow>After your questionnaire</Eyebrow>
                    <h1 className="text-[2.4rem] sm:text-6xl font-black tracking-tight leading-[1.02] mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 pb-1">
                        One buyer.<br />One plant.<br />Still on Tally.
                    </h1>
                    <p className="text-white/80 text-lg sm:text-2xl font-medium leading-snug mb-8">
                        Every answer you gave points at the same person.
                    </p>
                    <div>
                        <Row k="Industry" v="Engineering manufacturers: machine builders, equipment makers, heavy engineering, components." />
                        <Row k="Size" v="₹50 Cr turnover and above. Smaller companies waste your sales team's time." />
                        <Row k="Who signs" v="The owner. All three of your last buyers." />
                        <Row k="Today" v="Tally, Google Sheets, Excel, or an old custom system." />
                        <Row k="Region" v="Pan India." />
                    </div>
                </Slide>

                {/* 2. The position */}
                <Slide id="position">
                    <Eyebrow>The position</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8 text-emerald-300">
                        The ERP built around how an engineering plant runs.
                    </h2>
                    <p className="text-white/70 text-base sm:text-xl leading-relaxed mb-8">
                        Owners believe every ERP is the same. Most are accounting software with modules added on. New Horizon starts from the BOM, the job work and the shop floor. That gap is the story, told without naming anyone.
                    </p>
                    <div>
                        <Row tone="emerald" k="21+ years" v="Your longest continuous client. Median client: 11+ years." />
                        <Row tone="emerald" k="50 → 140" v="A Tally-only client went live in 6 months on 50 users and runs 140 licences three years later." />
                        <Row tone="emerald" k="50+ machines" v="Injection moulding machines writing to the ERP live, with no manual entry." />
                        <Row tone="emerald" k="12+ MNCs" v="Indian subsidiaries of global companies run SourcePro here." />
                    </div>
                </Slide>

                {/* 4. The cycle */}
                <Slide id="cycle">
                    <Eyebrow>The content cycle</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        Every 7 videos<br />do three different jobs.
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                        <Mix n="2" label="Reach" sub="Wide business topics. Bring new owners to the page." tone="text-indigo-300" />
                        <Mix n="3" label="Education" sub="One plant problem solved per video. Builds trust." tone="text-emerald-300" />
                        <Mix n="2" label="Sales" sub="Client proof and the offer. Turns trust into a call." tone="text-amber-300" />
                    </div>
                </Slide>

                {/* 5. Reach */}
                <Slide id="reach">
                    <Eyebrow>The 2 reach videos</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        Not about SourcePro.<br /><span className="text-indigo-300">About Indian business.</span>
                    </h2>
                    <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-6">
                        Reach videos stay away from software entirely. They are wide business stories told through factories, brands and family businesses. The platform shows a video to people who watch that topic, so a factory story finds business owners.
                    </p>
                    <div>
                        <Row k="Brand teardown" v="How Parle-G still makes money on a ₹5 packet." />
                        <Row k="Family business" v="Why most family businesses don't survive the second generation." />
                        <Row k="India vs China" v="Why a Chinese factory can quote cheaper, and where Indian plants win." />
                        <Row k="Money myths" v="Why a profitable company still runs out of cash." />
                        <Row k="Inside the plant" v="How a car leaves an assembly line every minute." />
                    </div>
                </Slide>

                {/* 6. Trust and sales */}
                <Slide id="trust">
                    <Eyebrow>The 3 education and 2 sales videos</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        Here the plant<br />is the topic.
                    </h2>
                    <div className="mb-6">
                        <Row tone="emerald" k="Education" v="What MRP tells you before you buy raw material." />
                        <Row tone="emerald" k="Education" v="Count your concurrent users before you buy: 100 named users often need about 65 licences." />
                        <Row tone="emerald" k="Education" v="Material sent for job work comes back short. The one step that stops it." />
                        <Row tone="amber" k="Sales" v="Your 50 to 140 user client, in the client's own words and face." />
                        <Row tone="amber" k="Sales" v="Who New Horizon is for and who it isn't, then a free Plant Diagnostic." />
                    </div>
                    <p className="text-white/60 text-base sm:text-lg leading-relaxed">
                        The myths you listed become their own videos: all ERPs are the same, changes are small work, software is copy-paste.
                    </p>
                </Slide>

                {/* 7. The loops */}
                <Slide id="loops">
                    <Eyebrow>How a viewer becomes a demo</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        Every video hands<br />the viewer to the next one.
                    </h2>
                    <div>
                        <Row k="Video to video" v="Each video solves one problem and opens the next. A cash-flow story leads to raw material bought too early, which leads to MRP, then a client story." />
                        <Row k="Diagnostic" v="The sales videos offer a free Plant Diagnostic: where your plant leaks time and stock. One form with turnover and industry, so only the right owners reach your team." />
                        <Row k="60 seconds" v="A WhatsApp reaches the owner within a minute of the form. Your regional team calls next, as it does today." />
                        <Row tone="emerald" k="Nurture" v="Longer YouTube walk-throughs, ads shown again to people who watched, and client stories sent to your existing manufacturer database." />
                        <Row tone="emerald" k="Google" v="Before signing, an owner searches your name. Client stories and testimonials fill that first page." />
                        <Row tone="amber" k="Learning" v="Your team asks every prospect which video made them reach out. We make more of that." />
                    </div>
                </Slide>

                {/* 8. The face */}
                <Slide id="face">
                    <Eyebrow>Who is on screen</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        Your AI clone teaches.<br /><span className="text-emerald-300">Your clients sell.</span>
                    </h2>
                    <div className="mb-6">
                        <Row k="The clone" v="You chose an AI clone over a faceless page. We recommend the clone be you: an owner speaking to owners, without you filming anything after day one." />
                        <Row k="Real screens" v="Education videos show New Horizon itself, so the clone explains and the screen proves it." />
                        <Row tone="emerald" k="Real clients" v="Sales videos use your client testimonials. A peer's face closes faster than any presenter." />
                    </div>
                    <div>
                        <Row tone="amber" k="Instagram" v="All 7 videos. The comment and DM path to the diagnostic." />
                        <Row tone="amber" k="YouTube" v="The same videos plus longer module walk-throughs an owner can watch before a demo." />
                        <Row tone="amber" k="Ads" v="The sales videos and the diagnostic, shown to people who already watched." />
                        <Row tone="amber" k="LinkedIn" v="Company page carries every video." />
                    </div>
                </Slide>

                {/* 9. Your time */}
                <Slide id="time">
                    <Eyebrow>What it takes from you</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        Two hours once.<br />Fifteen minutes a week.
                    </h2>
                    <div className="mb-8">
                        <Row k="Once" v="One recording session of about 2 hours to build your clone." />
                        <Row k="Monthly" v="A 30-minute call to pick the month's topics." />
                        <Row k="Weekly" v="About 15 minutes on WhatsApp to approve scripts." />
                        <Row k="Your team" v="One product person checks technical claims. Sales shares the questions prospects ask." />
                    </div>
                    <Eyebrow>Still needed from your side</Eyebrow>
                    <div>
                        <Row tone="rose" k="Lead cost" v="Rough cost per qualified lead today from ads, email and calls." />
                        <Row tone="rose" k="Files" v="The corporate deck and 2 or 3 client testimonial videos." />
                        <Row tone="rose" k="Screens" v="A New Horizon demo login for screen recordings." />
                    </div>
                </Slide>

                {/* 10. Next */}
                <Slide id="next">
                    <Eyebrow>Next</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        A system that runs<br /><span className="text-emerald-300">while you run the plant.</span>
                    </h2>
                    <div className="mb-10">
                        <Row k="We run" v="Research, scripts, clone videos, edits, posting, ads creative and the diagnostic." />
                        <Row tone="emerald" k="You run" v="The demo and the close. Your team already calls within the hour, the hardest part to get right." />
                    </div>
                    <p className="text-indigo-300 text-base sm:text-xl font-bold leading-snug">
                        One question to start with.<br />
                        <span className="text-white">Which client story do we lead with?</span>
                    </p>
                </Slide>
            </div>

            {active === 0 && (
                <div className="fixed bottom-7 left-1/2 -translate-x-1/2 z-40 text-white/40 animate-bounce pointer-events-none">
                    <ChevronDown className="w-6 h-6" />
                </div>
            )}
        </div>
    );
}
